import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus, X, Send, Search, UserPlus, FileText, HelpCircle, CreditCard, AlertTriangle, ChevronDown, User as UserIcon, ListOrdered, StickyNote, Calculator, Loader2 } from 'lucide-react';

import NavigationBar from '../components/NavigationBar';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';
import LoadingSpinner from '../components/LoadingSpinner';
import { InfoTooltip } from '../components/InfoTooltip';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import apiService from '../services/api';
import { Appointment, User } from '../types';
import { shouldShowBusinessDashboard } from '../utils/navigationUtils';
import { searchCustomers as fuzzySearchCustomers } from '../utils/searchUtils';
import { displayCustomerPhone, formatPhoneNumber } from '../utils/phoneFormatUtils';
import { canViewFullCustomerPhone } from '../utils/roleUtils';

type InvoiceStatus = 'DRAFT' | 'SENT' | 'PAID' | 'VOIDED';
type ApiInvoiceStatus = 'DRAFT' | 'SENT' | 'PAID';
type CollectionMethod = 'send_invoice' | 'charge_now';

const toApiInvoiceStatus = (s?: InvoiceStatus): ApiInvoiceStatus | undefined => {
  if (s === 'DRAFT' || s === 'SENT' || s === 'PAID') return s;
  return undefined;
};

type InvoiceLineItem = {
  clientId: string;
  included: boolean;
  description: string;
  qty: number;
  /** Money input stored as "cents digits" (e.g. "123" => $1.23) */
  unitPriceCents: string;
  professionalName?: string;
  serviceId?: string;
  assignmentId?: string;
  durationMinutes?: number;
  fromDateTime?: string;
  toDateTime?: string;
  source?: 'booking' | 'custom';
};

const toDateInputValue = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatMoney = (n: number): string => {
  const v = Number.isFinite(n) ? n : 0;
  return v.toFixed(2);
};

const safeNum = (v: any): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

/** "Cents entry" behavior: store only digits, display as dollars with 2 decimals. */
const centsDigitsToAmount = (digits: string): number => {
  const only = String(digits || '').replace(/\D/g, '');
  const cents = only ? parseInt(only, 10) : 0;
  return Number.isFinite(cents) ? cents / 100 : 0;
};

const centsDigitsToMoneyString = (digits: string): string => {
  const only = String(digits || '').replace(/\D/g, '');
  if (!only) return '';
  const cents = parseInt(only, 10);
  return formatMoney((Number.isFinite(cents) ? cents : 0) / 100);
};

const moneyToCentsDigits = (amount: any): string => {
  const n = safeNum(amount);
  const cents = Math.round(Math.max(0, n) * 100);
  return cents > 0 ? String(cents) : '';
};

/** Percent entry stored as "basis points digits" (e.g. "825" => 8.25%). */
const percentDigitsToPercent = (digits: string): number => {
  const only = String(digits || '').replace(/\D/g, '');
  const bps = only ? parseInt(only, 10) : 0;
  return Number.isFinite(bps) ? bps / 100 : 0;
};

const percentDigitsToPercentString = (digits: string): string => {
  const only = String(digits || '').replace(/\D/g, '');
  if (!only) return '';
  const bps = parseInt(only, 10);
  const pct = Number.isFinite(bps) ? bps / 100 : 0;
  return pct.toFixed(2);
};

const percentToPercentDigits = (pct: any): string => {
  const n = safeNum(pct);
  const bps = Math.round(Math.max(0, n) * 100);
  return bps > 0 ? String(bps) : '';
};

const getDisplayName = (first?: string, last?: string, fallback?: string): string => {
  const full = `${first || ''} ${last || ''}`.trim();
  return full || fallback || '';
};

const collectionTabClipLeft: React.CSSProperties = {
  clipPath: 'polygon(0 0, 100% 0, calc(100% - 1rem) 100%, 0 100%)',
};
const collectionTabClipRight: React.CSSProperties = {
  clipPath: 'polygon(1rem 0, 100% 0, 100% 100%, 0 100%)',
};

const CreateInvoicePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const { user } = useAuth();
  const { currentBusiness, isInitialized: businessInitialized } = useBusiness();
  const isManual = !appointmentId;

  const fromPath = useMemo(() => {
    const st: any = location.state;
    return typeof st?.from === 'string' ? st.from : '';
  }, [location.state]);

  const cameFromBookingSelection = useMemo(() => {
    return typeof fromPath === 'string' && fromPath.includes('/dashboard/business/invoices/booking-selection');
  }, [fromPath]);

  const prefill = useMemo(() => {
    const st: any = location.state;
    const p = st?.prefill;
    if (!p || typeof p !== 'object') return null;
    return p as Partial<{
      billTo: string;
      customerEmail: string;
      invoiceDate: string;
      dueDate: string;
      status: InvoiceStatus;
      notes: string;
    }>;
  }, [location.state]);

  const goBack = useCallback(() => {
    // If we arrived here from a specific page (calendar/details), go back there.
    if (fromPath) {
      navigate(fromPath);
      return;
    }
    // Otherwise, use history if available.
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    // Safe fallback: business appointments/calendar section.
    navigate('/dashboard/business?section=appointments');
  }, [fromPath, navigate]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const toggleSidebar = () => setIsSidebarCollapsed(!isSidebarCollapsed);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [missingBusiness, setMissingBusiness] = useState(false);

  const [billTo, setBillTo] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  /** When true, show Customer Name and Customer Email fields (e.g. after "New Customer") */
  const [showNewCustomerFields, setShowNewCustomerFields] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState<User[]>([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<User | null>(null);
  const customerSearchContainerRef = useRef<HTMLDivElement>(null);
  const [searchInputReadOnly, setSearchInputReadOnly] = useState(true);
  const [emailLookupUser, setEmailLookupUser] = useState<User | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(() => toDateInputValue(new Date().toISOString()));
  const [dueDate, setDueDate] = useState(() => toDateInputValue(new Date().toISOString()));
  const [collectionMethod, setCollectionMethod] = useState<CollectionMethod>('send_invoice');
  const [onlinePaymentsEnabled, setOnlinePaymentsEnabled] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState('');
  const [status, setStatus] = useState<InvoiceStatus>('DRAFT');
  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  const [discount, setDiscount] = useState<string>('');
  /** Tax percentage stored as basis-points digits (e.g. "825" => 8.25%) */
  const [taxPercent, setTaxPercent] = useState<string>('');
  const [includeTip, setIncludeTip] = useState(true);
  /** Tip amount stored as cents digits (same as discount) */
  const [tip, setTip] = useState<string>('');
  /** Optional note regarding the tip (private only) */
  const [tipNote, setTipNote] = useState('');

  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([]);

  const [createdInvoiceId, setCreatedInvoiceId] = useState<string | null>(null);
  const [businessServices, setBusinessServices] = useState<Array<{ id: string; name: string; price?: number | null }>>([]);
  const [openItemDropdownClientId, setOpenItemDropdownClientId] = useState<string | null>(null);
  const [itemDropdownAnchorRect, setItemDropdownAnchorRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  const createClientId = () => (crypto as any).randomUUID?.() || `${Date.now()}-${Math.random()}`;

  const loadData = useCallback(async () => {
    if (!currentBusiness?.id) {
      // Business selection is resolved asynchronously; avoid firing a toast during initial load.
      if (!businessInitialized) return;
      setMissingBusiness(true);
      setLoading(false);
      return;
    }

    setMissingBusiness(false);
    setLoading(true);

    // Manual invoices don't have an appointment source; start with a blank editor.
    if (!appointmentId) {
      setAppointment(null);
      setLineItems([]);
      setLoading(false);
      return;
    }

    try {
      const apt = await apiService.getAppointment(appointmentId);
      setAppointment(apt);

      const billToName = `${apt.customer_first_name || ''} ${apt.customer_last_name || ''}`.trim();
      setBillTo(billToName || 'Customer');
      setCustomerEmail((apt.customer_email || '').trim());
      const phoneVal = (apt.customer_phone || (apt as any).customer_phone || '').trim();
      const formattedPhone = displayCustomerPhone(phoneVal, canViewFullCustomerPhone(user, currentBusiness?.id));
      setCustomerPhone(formattedPhone);

      // Show customer card when we have appointment customer info (card only renders when selectedCustomer is set).
      const hasCustomerInfo = (billToName || (apt.customer_email || '').trim() || phoneVal);
      if (hasCustomerInfo) {
        const appointmentCustomerUser: User = {
          id: (apt as any).customerId || `appointment-customer-${apt.id}`,
          firstName: (apt.customer_first_name || '').trim() || 'Customer',
          lastName: (apt.customer_last_name || '').trim(),
          email: (apt.customer_email || '').trim(),
          phoneNumber: formattedPhone || undefined,
          roles: [],
          isEmailVerified: false,
          createdAt: '',
          updatedAt: '',
        };
        setSelectedCustomer(appointmentCustomerUser);
      }

      // Keep invoice date and due date as "today" by default (date-only, no timezone shift).
      // Do not auto-override due date to the appointment date.

      const assignments: any[] = (apt as any).assignments || [];
      const fromBooking: InvoiceLineItem[] = assignments.map((a) => {
        const professionalName =
          a.professionalName ||
          getDisplayName(a.professionalFirstName, a.professionalLastName, a.professionalEmail);
        return {
          clientId: a.id || (crypto as any).randomUUID?.() || `${Date.now()}-${Math.random()}`,
          included: true,
          description: a.serviceName || 'Service',
          qty: 1,
          unitPriceCents: moneyToCentsDigits(a.price),
          professionalName: professionalName || undefined,
          serviceId: a.serviceId,
          assignmentId: a.id,
          durationMinutes: a.durationMinutes,
          fromDateTime: a.fromDateTime,
          toDateTime: a.toDateTime,
          source: 'booking',
        };
      });
      setLineItems(fromBooking);
    } catch (e) {
      console.error('Error loading appointment for invoice:', e);
      toast.error('Failed to load appointment');
    } finally {
      setLoading(false);
    }
  }, [appointmentId, currentBusiness?.id, businessInitialized]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!currentBusiness?.id) return;
    let cancelled = false;
    apiService
      .getServices(currentBusiness.id)
      .then((list) => {
        if (!cancelled && Array.isArray(list)) {
          setBusinessServices(
            list
              .filter((s: any) => s?.deleted !== true)
              .map((s: any) => ({
                id: s.id,
                name: String(s.name ?? s.serviceName ?? '').trim(),
                price: s.price != null ? (Number.isFinite(Number(s.price)) ? Number(s.price) : null) : null,
              }))
              .filter((s) => s.name)
          );
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [currentBusiness?.id]);

  useEffect(() => {
    if (!currentBusiness?.id) {
      setOnlinePaymentsEnabled(false);
      return;
    }
    let cancelled = false;
    apiService
      .getBusinessStripeAccount(currentBusiness.id)
      .then((data) => {
        if (!cancelled) setOnlinePaymentsEnabled(!!(data?.chargesEnabled));
      })
      .catch(() => {
        if (!cancelled) setOnlinePaymentsEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentBusiness?.id]);

  useEffect(() => {
    if (!openItemDropdownClientId) {
      setItemDropdownAnchorRect(null);
      return;
    }
    const updateRect = () => {
      const candidates = Array.from(
        document.querySelectorAll(`[data-item-dropdown="${openItemDropdownClientId}"]`)
      );
      for (let i = 0; i < candidates.length; i++) {
        const r = candidates[i].getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          setItemDropdownAnchorRect({ top: r.top, left: r.left, width: r.width, height: r.height });
          return;
        }
      }
      setItemDropdownAnchorRect(null);
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
      setItemDropdownAnchorRect(null);
    };
  }, [openItemDropdownClientId]);

  useEffect(() => {
    if (!openItemDropdownClientId) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const list = document.querySelector('[data-item-dropdown-list]');
      if (list && list.contains(target)) return;
      const candidates = Array.from(
        document.querySelectorAll(`[data-item-dropdown="${openItemDropdownClientId}"]`)
      );
      for (let i = 0; i < candidates.length; i++) {
        if (candidates[i].contains(target)) return;
      }
      setOpenItemDropdownClientId(null);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [openItemDropdownClientId]);

  const prefillAppliedRef = React.useRef(false);
  const preservedFormRestoredRef = React.useRef(false);

  useEffect(() => {
    if (!prefill) return;
    if (prefillAppliedRef.current) return;
    prefillAppliedRef.current = true;

    // For booking-based invoices, bill-to and customer email come from the appointment.
    // For manual invoices, allow prefilling these from the modal.
    if (isManual) {
      if (typeof prefill.billTo === 'string') setBillTo(prefill.billTo);
      if (typeof prefill.customerEmail === 'string') setCustomerEmail(prefill.customerEmail);
      if (typeof (prefill as any).customerPhone === 'string') setCustomerPhone(formatPhoneNumber((prefill as any).customerPhone));
    }
    if (typeof prefill.invoiceDate === 'string') setInvoiceDate(prefill.invoiceDate);
    if (typeof prefill.dueDate === 'string') setDueDate(prefill.dueDate);
    if (typeof prefill.status === 'string') setStatus(prefill.status);
    if (typeof prefill.notes === 'string') setNotes(prefill.notes);
  }, [isManual, prefill]);

  const preservedFormStateFromState = (location.state as any)?.preservedFormState;
  const createdInvoiceIdFromState = (location.state as any)?.createdInvoiceId;
  const clearedCreateInvoiceStorageRef = React.useRef(false);
  if (!preservedFormStateFromState && typeof sessionStorage !== 'undefined' && !clearedCreateInvoiceStorageRef.current) {
    clearedCreateInvoiceStorageRef.current = true;
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('createInvoiceFormState_')) keysToRemove.push(key);
      }
      keysToRemove.forEach((k) => sessionStorage.removeItem(k));
    } catch {
      // ignore
    }
  }
  const preservedFormState =
    preservedFormStateFromState && typeof preservedFormStateFromState === 'object'
      ? preservedFormStateFromState
      : typeof createdInvoiceIdFromState === 'string' && createdInvoiceIdFromState.trim()
        ? (() => {
            try {
              const raw = sessionStorage.getItem(`createInvoiceFormState_${createdInvoiceIdFromState.trim()}`);
              return raw ? JSON.parse(raw) : undefined;
            } catch {
              return undefined;
            }
          })()
        : undefined;
  useEffect(() => {
    if (!preservedFormState || typeof preservedFormState !== 'object' || preservedFormRestoredRef.current || loading) return;
    preservedFormRestoredRef.current = true;
    if (typeof preservedFormState.billTo === 'string') setBillTo(preservedFormState.billTo);
    if (typeof preservedFormState.customerEmail === 'string') setCustomerEmail(preservedFormState.customerEmail);
    if (typeof preservedFormState.customerPhone === 'string') setCustomerPhone(formatPhoneNumber(preservedFormState.customerPhone));
    if (typeof preservedFormState.invoiceDate === 'string') setInvoiceDate(preservedFormState.invoiceDate);
    if (typeof preservedFormState.dueDate === 'string') setDueDate(preservedFormState.dueDate);
    if (preservedFormState.collectionMethod === 'send_invoice' || preservedFormState.collectionMethod === 'charge_now') setCollectionMethod(preservedFormState.collectionMethod);
    if (typeof preservedFormState.receiptEmail === 'string') setReceiptEmail(preservedFormState.receiptEmail);
    if (typeof preservedFormState.notes === 'string') setNotes(preservedFormState.notes);
    if (typeof preservedFormState.internalNotes === 'string') setInternalNotes(preservedFormState.internalNotes);
    if (typeof preservedFormState.discount === 'string') setDiscount(preservedFormState.discount);
    if (typeof preservedFormState.taxPercent === 'string') setTaxPercent(preservedFormState.taxPercent);
    if (typeof preservedFormState.includeTip === 'boolean') setIncludeTip(preservedFormState.includeTip);
    if (typeof preservedFormState.tip === 'string') setTip(preservedFormState.tip);
    if (typeof preservedFormState.tipNote === 'string') setTipNote(preservedFormState.tipNote);
    if (typeof preservedFormState.showNewCustomerFields === 'boolean') setShowNewCustomerFields(preservedFormState.showNewCustomerFields);
    const snapshot = preservedFormState.selectedCustomerSnapshot;
    if (snapshot && typeof snapshot === 'object' && snapshot.id && (snapshot.firstName != null || snapshot.email != null)) {
      const restoredUser: User = {
        id: String(snapshot.id),
        firstName: String(snapshot.firstName ?? (snapshot.email ?? '').split('@')[0] ?? ''),
        lastName: String(snapshot.lastName ?? ''),
        email: String(snapshot.email ?? ''),
        phoneNumber: typeof snapshot.phoneNumber === 'string' ? snapshot.phoneNumber : undefined,
        roles: [],
        isEmailVerified: false,
        createdAt: '',
        updatedAt: '',
      };
      setSelectedCustomer(restoredUser);
    }
    if (Array.isArray(preservedFormState.lineItems) && preservedFormState.lineItems.length > 0) {
      setLineItems(
        preservedFormState.lineItems.map((li: any) => ({
          clientId: li.clientId || createClientId(),
          included: !!li.included,
          description: typeof li.description === 'string' ? li.description : '',
          qty: typeof li.qty === 'number' ? li.qty : 1,
          unitPriceCents: typeof li.unitPriceCents === 'string' ? li.unitPriceCents : '',
          professionalName: li.professionalName,
          serviceId: li.serviceId,
          assignmentId: li.assignmentId,
          durationMinutes: li.durationMinutes,
          fromDateTime: li.fromDateTime,
          toDateTime: li.toDateTime,
          source: li.source || 'custom',
        }))
      );
    }
    const id = (location.state as any)?.createdInvoiceId;
    if (typeof id === 'string' && id.trim()) setCreatedInvoiceId(id.trim());
  }, [preservedFormState, loading]);

  const formatCustomerEmail = (email: string | undefined | null): string => {
    if (!email) return 'No contact provided';
    if (email.startsWith('temp') && email.includes('@appointment.local')) return 'No contact provided';
    return email;
  };
  const getCustomerPhone = (c: User | null | undefined): string | undefined => {
    const phone = c?.phone ?? (c as any)?.phoneNumber ?? (c as any)?.phone_number;
    if (typeof phone !== 'string' || !phone.trim()) return undefined;
    return displayCustomerPhone(phone, canViewFullCustomerPhone(user, currentBusiness?.id));
  };

  const searchCustomers = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setCustomerSearchResults([]);
      return;
    }
    const businessId = currentBusiness?.id;
    if (!businessId) {
      setCustomerSearchResults([]);
      return;
    }
    setSearchingCustomers(true);
    try {
      // Scoped to customers with appointments, quotes, or invoices for this business
      const backendResults = await apiService.searchUsers(query, 100, { businessId });
      const fuzzyResults = fuzzySearchCustomers(backendResults, query);
      setCustomerSearchResults(fuzzyResults.slice(0, 10) as User[]);
    } catch (err) {
      console.error('Error searching customers:', err);
      setCustomerSearchResults([]);
    } finally {
      setSearchingCustomers(false);
    }
  }, [currentBusiness?.id]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (customerSearch && !selectedCustomer) {
        searchCustomers(customerSearch);
      } else {
        setCustomerSearchResults([]);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [customerSearch, selectedCustomer, searchCustomers]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (customerSearchContainerRef.current && !customerSearchContainerRef.current.contains(e.target as Node)) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showNewCustomerFields || !customerEmail.trim() || !customerEmail.includes('@')) {
      setEmailLookupUser(null);
      return;
    }
    const timeoutId = setTimeout(async () => {
      const email = customerEmail.toLowerCase().trim();
      setCheckingEmail(true);
      try {
        const result = await apiService.checkEmailExists(email);
        if (result.exists && result.user) {
          const u = result.user;
          setEmailLookupUser({
            id: u.id,
            email: u.email,
            firstName: u.firstName,
            lastName: u.lastName,
            phoneNumber: (result.user as any).phoneNumber ?? (result.user as any).phone,
            roles: [],
            isEmailVerified: false,
            createdAt: '',
            updatedAt: '',
          } as User);
        } else {
          setEmailLookupUser(null);
        }
      } catch (err) {
        console.error('Error checking email:', err);
        setEmailLookupUser(null);
      } finally {
        setCheckingEmail(false);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [showNewCustomerFields, customerEmail]);

  const { subTotal, total } = useMemo(() => {
    const subtotal = lineItems.reduce((sum, li) => {
      if (!li.included) return sum;
      const qty = Math.max(0, safeNum(li.qty));
      const price = Math.max(0, centsDigitsToAmount(li.unitPriceCents));
      return sum + qty * price;
    }, 0);
    const d = Math.max(0, centsDigitsToAmount(discount));
    const afterDiscount = Math.max(0, subtotal - d);
    const taxPct = Math.max(0, percentDigitsToPercent(taxPercent));
    const taxAmt = Math.round(afterDiscount * (taxPct / 100) * 100) / 100;
    const tipAmt = includeTip ? Math.max(0, centsDigitsToAmount(tip)) : 0;
    const computedTotal = afterDiscount + taxAmt + tipAmt;
    return { subTotal: subtotal, total: computedTotal };
  }, [lineItems, discount, taxPercent, includeTip, tip]);

  const canSave = useMemo(() => {
    if (!currentBusiness?.id) return false;
    if (!billTo.trim()) return false;
    const anyIncluded = lineItems.some((li) => li.included && safeNum(li.qty) > 0);
    return anyIncluded;
  }, [currentBusiness?.id, billTo, lineItems]);

  const canSendInvoice = useMemo(() => {
    const s = String(status || '').toUpperCase();
    return canSave && !saving && !['SENT', 'PAID', 'VOIDED'].includes(s) && !!customerEmail.trim();
  }, [canSave, saving, status, customerEmail]);

  const handleAddCustomLine = () => {
    const id = createClientId();
    setLineItems((prev) => [
      ...prev,
      {
        clientId: id,
        included: true,
        description: '',
        qty: 1,
        unitPriceCents: '',
        source: 'custom',
      },
    ]);
  };

  const updateLine = (clientId: string, patch: Partial<InvoiceLineItem>) => {
    setLineItems((prev) => prev.map((li) => (li.clientId === clientId ? { ...li, ...patch } : li)));
  };

  const removeLine = (clientId: string) => {
    setLineItems((prev) => prev.filter((li) => li.clientId !== clientId));
  };

  const handleSave = async (forceStatus?: InvoiceStatus) => {
    if (!currentBusiness?.id) {
      toast.error('No business selected');
      return;
    }
    if (!canSave) {
      toast.error('Please include at least one line item and complete required fields');
      return;
    }

    if (forceStatus === 'SENT' && !customerEmail.trim()) {
      toast.error('Customer email is required to send the invoice');
      return;
    }

    if (collectionMethod === 'charge_now' && createdInvoiceId) {
      const formState = {
        billTo,
        customerEmail,
        customerPhone,
        invoiceDate,
        dueDate,
        collectionMethod,
        receiptEmail,
        notes,
        internalNotes,
        discount,
        taxPercent,
        includeTip,
        tip,
        tipNote,
        showNewCustomerFields,
        selectedCustomerSnapshot: selectedCustomer
          ? {
              id: selectedCustomer.id,
              firstName: selectedCustomer.firstName,
              lastName: selectedCustomer.lastName,
              email: selectedCustomer.email,
              phoneNumber: getCustomerPhone(selectedCustomer) ?? selectedCustomer.phoneNumber ?? undefined,
            }
          : null,
        lineItems: lineItems.map((li) => ({
          clientId: li.clientId,
          included: li.included,
          description: li.description,
          qty: li.qty,
          unitPriceCents: li.unitPriceCents,
          professionalName: li.professionalName,
          serviceId: li.serviceId,
          assignmentId: li.assignmentId,
          durationMinutes: li.durationMinutes,
          fromDateTime: li.fromDateTime,
          toDateTime: li.toDateTime,
          source: li.source,
        })),
      };
      try {
        sessionStorage.setItem(`createInvoiceFormState_${createdInvoiceId}`, JSON.stringify(formState));
      } catch {
        // ignore
      }
      navigate(`/dashboard/business/invoices/${createdInvoiceId}/charge`, {
        replace: true,
        state: { formState },
      });
      return;
    }

    setSaving(true);
    try {
      const items = lineItems.map((li) => {
        const qty = Math.max(0, safeNum(li.qty));
        const unitPrice = Math.max(0, centsDigitsToAmount(li.unitPriceCents));
        const amount = li.included ? qty * unitPrice : 0;
        return {
          included: !!li.included,
          description: (li.description || '').trim(),
          qty,
          unitPrice,
          amount,
          professionalName: li.professionalName || null,
          serviceId: li.serviceId || null,
          assignmentId: li.assignmentId || null,
          durationMinutes: li.durationMinutes ?? null,
          fromDateTime: li.fromDateTime ?? null,
          toDateTime: li.toDateTime ?? null,
          source: li.source || 'custom',
        };
      });

      const subTotalAmt = items.reduce((sum, s) => sum + safeNum((s as any).amount), 0);
      const discountAmt = Math.max(0, centsDigitsToAmount(discount));
      const afterDiscount = Math.max(0, subTotalAmt - discountAmt);
      const taxPct = Math.max(0, percentDigitsToPercent(taxPercent));
      const taxAmt = Math.round(afterDiscount * (taxPct / 100) * 100) / 100;
      const tipAmt = includeTip ? Math.max(0, centsDigitsToAmount(tip)) : 0;

      // When "sending", create as DRAFT first; the send-email endpoint will mark as SENT only on success.
      const statusForApi: InvoiceStatus | undefined = forceStatus === 'SENT' ? 'DRAFT' : (forceStatus || status);

      const payload = {
        businessId: currentBusiness.id,
        ...(appointmentId ? { appointmentId } : {}),
        billTo: billTo.trim(),
        customerEmail: customerEmail.trim() || null,
        customerPhone: customerPhone.trim() || null,
        receiptEmail: collectionMethod === 'charge_now' ? (receiptEmail.trim() || null) : null,
        invoiceDate,
        dueDate,
        status: toApiInvoiceStatus(statusForApi),
        notes: notes.trim() || null,
        internalNotes: internalNotes.trim() || null,
        discount: discountAmt,
        tax: taxAmt, // server expects amount, UI captures percent
        tip: tipAmt,
        tipNote: includeTip ? (tipNote.trim() || null) : null,
        services: items,
      };

      const created = await apiService.createInvoice(payload);
      const createdId = created?.id;
      if (forceStatus === 'SENT' && createdId) {
        await apiService.sendInvoiceEmail(createdId);
        toast.success(`Invoice emailed to ${customerEmail.trim()}`);
        navigate('/dashboard/business?section=invoices', { replace: true });
        return;
      } else if (collectionMethod === 'charge_now' && createdId) {
        const formState = {
          billTo,
          customerEmail,
          customerPhone,
          invoiceDate,
          dueDate,
          collectionMethod,
          receiptEmail,
          notes,
          internalNotes,
          discount,
          taxPercent,
          includeTip,
          tip,
          tipNote,
          showNewCustomerFields,
          selectedCustomerSnapshot: selectedCustomer
            ? {
                id: selectedCustomer.id,
                firstName: selectedCustomer.firstName,
                lastName: selectedCustomer.lastName,
                email: selectedCustomer.email,
                phoneNumber: getCustomerPhone(selectedCustomer) ?? selectedCustomer.phoneNumber ?? undefined,
              }
            : null,
          lineItems: lineItems.map((li) => ({
            clientId: li.clientId,
            included: li.included,
            description: li.description,
            qty: li.qty,
            unitPriceCents: li.unitPriceCents,
            professionalName: li.professionalName,
            serviceId: li.serviceId,
            assignmentId: li.assignmentId,
            durationMinutes: li.durationMinutes,
            fromDateTime: li.fromDateTime,
            toDateTime: li.toDateTime,
            source: li.source,
          })),
        };
        try {
          sessionStorage.setItem(`createInvoiceFormState_${createdId}`, JSON.stringify(formState));
        } catch {
          // ignore
        }
        toast.success('Invoice created');
        navigate(`/dashboard/business/invoices/${createdId}/charge`, {
          replace: true,
          state: { formState },
        });
        return;
      } else {
        toast.success('Invoice saved');
      }

      if (createdId && !(collectionMethod === 'charge_now')) {
        if (cameFromBookingSelection) {
          navigate('/dashboard/business?section=invoices', { replace: true });
        } else {
          goBack();
        }
      }
    } catch (e: any) {
      console.error('Error creating invoice:', e);
      toast.error(e?.response?.data?.error || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  if (missingBusiness) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-lg shadow-sm p-6 text-center">
          <div className="text-lg font-semibold text-gray-900">No business selected</div>
          <div className="text-sm text-gray-600 mt-2">
            Please select a business (or ensure your business role is active) to create an invoice.
          </div>
          <button onClick={goBack} className="mt-5 aacore-btn2-outline">
            Back
          </button>
        </div>
      </div>
    );
  }

  if (loading || (!appointment && !isManual)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <NavigationBar />

      <div className="flex-1 lg:grid lg:grid-cols-[auto_1fr] lg:min-h-screen">
        {shouldShowBusinessDashboard(user!) && (
          <BusinessDashboardSidebar
            isSidebarOpen={isSidebarOpen}
            isSidebarCollapsed={isSidebarCollapsed}
            activeSection="invoices"
            onClose={() => setIsSidebarOpen(false)}
            onToggleCollapse={toggleSidebar}
            unreadNotificationCount={0}
          />
        )}

        <div className="flex flex-col min-h-screen">
          <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <button onClick={() => setIsSidebarOpen(true)} className="p-2 rounded-md hover:bg-gray-100">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-lg font-semibold text-gray-900">Create Invoice</h1>
              <div className="w-10"></div>
            </div>
          </div>

          <div className="flex-1 p-4 md:p-8">
            <button
              onClick={goBack}
              className="text-gray-600 hover:text-gray-900 mb-4 flex items-center gap-2"
              disabled={saving}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h1 className="hidden lg:block text-2xl font-semibold text-gray-900">
                  Create Invoice
                </h1>
                <div className="text-sm text-gray-600 mt-1">
                  Send an invoice by email, or switch to Charge Now when you need to collect payment on the spot.
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <UserIcon className="w-5 h-5 text-gray-600" />
                    Customer
                  </h2>
                  <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    {selectedCustomer ? (
                      <div className="flex-1 flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="w-10 h-10 rounded-full bg-[#0B4778] flex items-center justify-center text-white text-sm font-semibold shrink-0">
                          {selectedCustomer.firstName?.[0]}{selectedCustomer.lastName?.[0]}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-gray-900 truncate">
                            {selectedCustomer.firstName} {selectedCustomer.lastName}
                          </p>
                          <p className="text-sm text-gray-600 truncate">{formatCustomerEmail(selectedCustomer.email)}</p>
                          {getCustomerPhone(selectedCustomer) && (
                            <p className="text-sm text-gray-600 truncate">{getCustomerPhone(selectedCustomer)}</p>
                          )}
                        </div>
                        {!appointmentId && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedCustomer(null);
                              setBillTo('');
                              setCustomerEmail('');
                              setCustomerPhone('');
                              setCustomerSearch('');
                              setCustomerSearchResults([]);
                              setSearchInputReadOnly(true);
                            }}
                            className="text-gray-500 hover:text-gray-700 p-1"
                            aria-label="Clear customer"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    ) : appointmentId ? (
                      <div className="flex-1 flex items-center p-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-500 text-sm">
                        Customer from appointment (no name or email on file)
                      </div>
                    ) : (
                      <div className="flex-1 relative" ref={customerSearchContainerRef}>
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          name="customer-search-query"
                          placeholder="Search existing customer by name, phone or email"
                          value={customerSearch}
                          readOnly={searchInputReadOnly}
                          onFocus={() => {
                            if (searchInputReadOnly) setSearchInputReadOnly(false);
                            setShowCustomerDropdown(true);
                          }}
                          onChange={(e) => {
                            setCustomerSearch(e.target.value);
                            setShowCustomerDropdown(true);
                          }}
                          autoComplete="nope"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck={false}
                          data-lpignore="true"
                          data-form-type="other"
                          className={`w-full pl-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 ${customerSearch ? 'pr-10' : 'pr-3'}`}
                          disabled={saving}
                        />
                        {customerSearch && (
                          <button
                            type="button"
                            onClick={() => {
                              setCustomerSearch('');
                              setCustomerSearchResults([]);
                              setShowCustomerDropdown(false);
                              setSearchInputReadOnly(true);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            aria-label="Clear search"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        {showCustomerDropdown && (customerSearchResults.length > 0 || searchingCustomers) && (
                          <div className="absolute z-50 w-full mt-1 left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                            {searchingCustomers ? (
                              <div className="px-4 py-3 text-gray-500 text-sm">Searching...</div>
                            ) : (
                              customerSearchResults.map((customer) => (
                                <div
                                  key={customer.id}
                                  onClick={() => {
                                    setSelectedCustomer(customer);
                                    setBillTo(`${customer.firstName} ${customer.lastName}`.trim());
                                    setCustomerEmail(customer.email || '');
                                    setCustomerPhone(formatPhoneNumber(getCustomerPhone(customer) || ''));
                                    if (collectionMethod === 'charge_now') setReceiptEmail(customer.email || '');
                                    setCustomerSearch('');
                                    setShowCustomerDropdown(false);
                                    setCustomerSearchResults([]);
                                  }}
                                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                                >
                                  <div className="w-8 h-8 rounded-full bg-[#0B4778] flex items-center justify-center text-white text-sm font-semibold shrink-0">
                                    {customer.firstName?.[0]}{customer.lastName?.[0]}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-medium text-gray-900 truncate">{customer.firstName} {customer.lastName}</p>
                                    <p className="text-sm text-gray-600 truncate">{formatCustomerEmail(customer.email)}</p>
                                    {getCustomerPhone(customer) && (
                                      <p className="text-sm text-gray-600 truncate">{getCustomerPhone(customer)}</p>
                                    )}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                        {showCustomerDropdown && customerSearch.length >= 2 && !searchingCustomers && customerSearchResults.length === 0 && (
                          <div className="absolute z-50 w-full mt-1 left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg px-4 py-3 text-gray-500 text-sm">
                            No customers found.
                          </div>
                        )}
                      </div>
                    )}
                    {!appointmentId && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewCustomerFields(true);
                          setSelectedCustomer(null);
                          setCustomerSearch('');
                          setCustomerSearchResults([]);
                          setShowCustomerDropdown(false);
                          setSearchInputReadOnly(true);
                          setBillTo('');
                          setCustomerEmail('');
                          setCustomerPhone('');
                          setEmailLookupUser(null);
                        }}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-white text-[#0B4778] font-semibold hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 shrink-0"
                        disabled={saving}
                      >
                        <UserPlus className="w-4 h-4" />
                        New Customer
                      </button>
                    )}
                  </div>
                  {showNewCustomerFields && !selectedCustomer && !appointmentId && (
                    <div className="mb-4 space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Customer Name</label>
                          <input
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={billTo}
                            onChange={(e) => setBillTo(e.target.value)}
                            disabled={saving}
                          />
                        </div>
                        <div className="relative">
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Customer email</label>
                          <input
                            type="email"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={customerEmail}
                            onChange={(e) => setCustomerEmail(e.target.value)}
                            disabled={saving}
                          />
                          {checkingEmail && (
                            <div className="absolute right-3 top-9 -translate-y-1/2">
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-[#0B4778]" />
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Customer Phone</label>
                          <input
                            type="tel"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            value={customerPhone}
                            onChange={(e) => setCustomerPhone(formatPhoneNumber(e.target.value))}
                            disabled={saving}
                            placeholder="(555) 123-4567"
                          />
                        </div>
                      </div>
                      {emailLookupUser && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                          <p className="text-sm text-amber-800 mb-2">
                            This email is associated with an existing customer. Select them below or enter a different email address.
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              const u = emailLookupUser;
                              setSelectedCustomer(u);
                              setBillTo(`${u.firstName} ${u.lastName}`.trim());
                              setCustomerEmail(u.email || '');
                              setCustomerPhone(formatPhoneNumber(getCustomerPhone(u) || ''));
                              if (collectionMethod === 'charge_now') setReceiptEmail(u.email || '');
                              setEmailLookupUser(null);
                              setShowNewCustomerFields(false);
                            }}
                            className="w-full flex items-center gap-3 p-3 rounded-lg border border-amber-300 bg-white hover:bg-amber-50/50 text-left"
                          >
                            <div className="w-10 h-10 rounded-full bg-[#0B4778] flex items-center justify-center text-white text-sm font-semibold shrink-0">
                              {emailLookupUser.firstName?.[0]}{emailLookupUser.lastName?.[0]}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900">
                                {emailLookupUser.firstName} {emailLookupUser.lastName}
                              </p>
                              <p className="text-sm text-gray-600 truncate">{formatCustomerEmail(emailLookupUser.email)}</p>
                              {getCustomerPhone(emailLookupUser) && (
                                <p className="text-sm text-gray-500 truncate">{getCustomerPhone(emailLookupUser)}</p>
                              )}
                            </div>
                            <span className="text-sm font-semibold text-[#0B4778] shrink-0">Select</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gray-600" />
                    Invoice Details
                  </h2>
                  <div className="mb-4">
                    <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 mb-2">
                      Collection Method
                      <span className="text-gray-400" title="Choose how to collect payment: send an invoice by email or charge the customer now.">
                        <HelpCircle className="w-4 h-4" />
                      </span>
                    </label>
                    <div
                      role="tablist"
                      aria-label="Collection method"
                      className="flex w-full rounded-lg bg-gray-100 p-1 overflow-hidden"
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={collectionMethod === 'send_invoice'}
                        onClick={() => setCollectionMethod('send_invoice')}
                        style={collectionTabClipLeft}
                        className={`flex-1 min-w-0 py-2.5 pl-4 pr-6 rounded-l-md text-sm font-semibold transition-all duration-150 inline-flex items-center justify-center gap-2 relative ${
                          collectionMethod === 'send_invoice'
                            ? 'bg-[#0B4778] text-white z-10'
                            : 'text-gray-600 hover:text-gray-800 z-0'
                        }`}
                        disabled={saving}
                      >
                        <Send className="w-4 h-4 shrink-0" />
                        Send Invoice
                      </button>
                      {!onlinePaymentsEnabled ? (
                        <InfoTooltip
                          triggerClassName="flex-1 flex min-w-0 -ml-4"
                          content={
                            <>
                              <span className="font-semibold">Payment processing is not available.</span>
                              <br />
                              Finish your payment setup before trying to charge a customer on the spot.
                            </>
                          }
                        >
                          <button
                            type="button"
                            role="tab"
                            aria-selected={false}
                            aria-disabled="true"
                            onClick={() => {}}
                            style={collectionTabClipRight}
                            className="w-full min-w-0 py-2.5 pl-6 pr-4 rounded-r-md text-sm font-semibold transition-all duration-150 inline-flex items-center justify-center gap-2 text-gray-400 cursor-not-allowed relative z-0"
                            disabled
                          >
                            <CreditCard className="w-4 h-4 shrink-0" />
                            Charge Now
                          </button>
                        </InfoTooltip>
                      ) : (
                        <button
                          type="button"
                          role="tab"
                          aria-selected={collectionMethod === 'charge_now'}
                          onClick={() => {
                            setCollectionMethod('charge_now');
                            if (!receiptEmail.trim()) setReceiptEmail(customerEmail);
                          }}
                          style={collectionTabClipRight}
                          className={`flex-1 min-w-0 -ml-4 py-2.5 pl-6 pr-4 rounded-r-md text-sm font-semibold transition-all duration-150 inline-flex items-center justify-center gap-2 relative ${
                            collectionMethod === 'charge_now'
                              ? 'bg-[#0B4778] text-white z-10'
                              : 'text-gray-600 hover:text-gray-800 z-0'
                          }`}
                          disabled={saving}
                        >
                          <CreditCard className="w-4 h-4 shrink-0" />
                          Charge Now
                        </button>
                      )}
                    </div>
                    {!onlinePaymentsEnabled && (
                      <p className="mt-2 text-xs text-amber-600 inline-flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        Payment not enabled — finish payment setup to charge on the spot.
                      </p>
                    )}
                  </div>
                  {collectionMethod === 'send_invoice' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Invoice Date</label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={invoiceDate}
                          onChange={(e) => setInvoiceDate(e.target.value)}
                          disabled={saving}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Due Date</label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={dueDate}
                          onChange={(e) => setDueDate(e.target.value)}
                          disabled={saving}
                        />
                      </div>
                    </div>
                  )}
                  {collectionMethod === 'charge_now' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Invoice Date</label>
                        <input
                          type="date"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={invoiceDate}
                          onChange={(e) => setInvoiceDate(e.target.value)}
                          disabled={saving}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Receipt Email</label>
                        <input
                          type="email"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Email to receive a copy of the paid invoice"
                          value={receiptEmail}
                          onChange={(e) => setReceiptEmail(e.target.value)}
                          disabled={saving}
                        />
                        <p className="text-xs text-gray-500 mt-1">A copy of the paid invoice will be sent to this email.</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <ListOrdered className="w-5 h-5 text-gray-600" />
                      Line items
                    </h2>
                    <button
                      type="button"
                      onClick={handleAddCustomLine}
                      className="aacore-btn2-secondary inline-flex items-center gap-2 text-sm disabled:opacity-50"
                      disabled={saving}
                    >
                      <Plus className="w-4 h-4" />
                      Add item
                    </button>
                  </div>

                  {lineItems.length === 0 && (
                    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />
                      Add at least one line item before an invoice can be created.
                    </div>
                  )}

                  {/* Mobile: card layout so item name, qty and price are never cut off */}
                  <div className="md:hidden space-y-4">
                    {lineItems.map((li) => {
                      const amount = li.included ? Math.max(0, safeNum(li.qty)) * Math.max(0, centsDigitsToAmount(li.unitPriceCents)) : 0;
                      const showDetails = !!(li.professionalName || li.source === 'booking');
                      return (
                        <div
                          key={li.clientId}
                          className={`border rounded-lg p-4 space-y-3 ${li.included ? 'border-gray-200 bg-gray-50/50' : 'border-gray-200 bg-gray-100/80 opacity-70'}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex items-center shrink-0">
                              <input
                                type="checkbox"
                                checked={li.included}
                                onChange={(e) => updateLine(li.clientId, { included: e.target.checked })}
                                disabled={saving}
                                className="h-4 w-4 accent-[#0B4778]"
                                aria-label="Include in invoice"
                              />
                            </div>
                            <div
                              className="flex-1 min-w-0 relative"
                              data-item-dropdown={li.clientId}
                            >
                              <input
                                className="w-full min-w-0 h-10 pl-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-semibold"
                                value={li.description}
                                onChange={(e) => updateLine(li.clientId, { description: e.target.value })}
                                onFocus={() => setOpenItemDropdownClientId(li.clientId)}
                                disabled={saving}
                                placeholder="Enter/search item name"
                              />
                              <button
                                type="button"
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-0.5 rounded"
                                onClick={() => setOpenItemDropdownClientId(li.clientId)}
                                disabled={saving}
                                aria-label="Show services"
                              >
                                <ChevronDown className="w-5 h-5" />
                              </button>
                            </div>
                            <div className="shrink-0">
                              <button
                                type="button"
                                onClick={() => removeLine(li.clientId)}
                                className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-red-600 transition-colors hover:bg-red-50 hover:border-red-300 hover:text-red-700 disabled:opacity-50"
                                disabled={saving}
                                title="Remove"
                                aria-label="Remove line item"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Qty</label>
                              <input
                                type="number"
                                min={0}
                                step={1}
                                className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0"
                                value={li.qty === 0 ? '' : li.qty}
                                onChange={(e) => updateLine(li.clientId, { qty: e.target.value === '' ? 0 : safeNum(e.target.value) })}
                                disabled={saving}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-500 mb-1">Unit price</label>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  className={`w-full h-10 pl-7 pr-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${!li.unitPriceCents || centsDigitsToAmount(li.unitPriceCents) === 0 ? 'bg-orange-50 border-amber-300' : 'border-gray-300'}`}
                                  placeholder="0.00"
                                  value={centsDigitsToMoneyString(li.unitPriceCents)}
                                  onChange={(e) => updateLine(li.clientId, { unitPriceCents: e.target.value.replace(/\D/g, '') })}
                                  disabled={saving}
                                />
                              </div>
                            </div>
                            <div className="text-right">
                              <label className="block text-xs font-medium text-gray-500 mb-1">Amount</label>
                              <div className="h-10 flex items-center justify-end font-semibold text-gray-900">
                                {li.included ? `$${formatMoney(amount)}` : '—'}
                              </div>
                            </div>
                          </div>
                          {showDetails && (
                            <div className="text-xs text-gray-500 pt-1 border-t border-gray-100">
                              {li.source === 'booking' ? 'From booking' : 'Custom'}
                              {li.professionalName ? ` • Professional: ${li.professionalName}` : ''}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop: flex rows — Item grows; Qty/Unit/Amount packed on the right */}
                  <div className="hidden md:block">
                    <div className="flex items-center gap-2 border-b border-gray-200 text-sm text-gray-500 py-2">
                      <div className="w-10 shrink-0 pl-2">Inc</div>
                      <div className="flex-1 min-w-0 pr-2">Item</div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <span className="w-14 text-center">Qty</span>
                        <span className="w-[5.25rem] text-center">Unit</span>
                        <span className="min-w-[4.5rem] text-right pr-1">Amount</span>
                      </div>
                      <div className="w-10 shrink-0" aria-hidden="true" />
                    </div>
                    {lineItems.map((li) => {
                      const amount = li.included ? Math.max(0, safeNum(li.qty)) * Math.max(0, centsDigitsToAmount(li.unitPriceCents)) : 0;
                      const showDetails = !!(li.professionalName || li.source === 'booking');
                      return (
                        <div key={li.clientId}>
                          <div
                            className={`flex items-center gap-2 py-2 ${showDetails ? '' : 'border-b border-gray-100'} ${!li.included ? 'bg-gray-100/80 opacity-70' : ''}`}
                          >
                            <div className="w-10 shrink-0 pl-2">
                              <div className="h-10 flex items-center">
                                <input
                                  type="checkbox"
                                  checked={li.included}
                                  onChange={(e) => updateLine(li.clientId, { included: e.target.checked })}
                                  disabled={saving}
                                  className="h-4 w-4 accent-[#0B4778]"
                                />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0 pr-2">
                              <div
                                className="relative min-w-0"
                                data-item-dropdown={li.clientId}
                              >
                                <input
                                  className="w-full min-w-0 h-10 pl-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-semibold"
                                  value={li.description}
                                  onChange={(e) => updateLine(li.clientId, { description: e.target.value })}
                                  onFocus={() => setOpenItemDropdownClientId(li.clientId)}
                                  disabled={saving}
                                  placeholder="Enter/search item name"
                                />
                                <button
                                  type="button"
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-0.5 rounded"
                                  onClick={() => setOpenItemDropdownClientId(li.clientId)}
                                  disabled={saving}
                                  aria-label="Show services"
                                >
                                  <ChevronDown className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <input
                                type="number"
                                min={0}
                                step={1}
                                className="w-14 shrink-0 h-10 px-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center"
                                placeholder="0"
                                value={li.qty === 0 ? '' : li.qty}
                                onChange={(e) => updateLine(li.clientId, { qty: e.target.value === '' ? 0 : safeNum(e.target.value) })}
                                disabled={saving}
                              />
                              <div className="relative h-10 w-[5.25rem] shrink-0">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">$</span>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  className={`w-full h-10 pl-5 pr-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right ${!li.unitPriceCents || centsDigitsToAmount(li.unitPriceCents) === 0 ? 'bg-orange-50 border-amber-300' : 'border-gray-300'}`}
                                  placeholder="0.00"
                                  value={centsDigitsToMoneyString(li.unitPriceCents)}
                                  onChange={(e) => updateLine(li.clientId, { unitPriceCents: e.target.value.replace(/\D/g, '') })}
                                  disabled={saving}
                                />
                              </div>
                              <div className="min-w-[4.5rem] shrink-0 h-10 flex items-center justify-end font-semibold text-gray-900 whitespace-nowrap pr-1">
                                {li.included ? `$${formatMoney(amount)}` : '—'}
                              </div>
                            </div>
                            <div className="w-10 shrink-0">
                              <div className="h-10 flex items-center justify-end">
                                <button
                                  type="button"
                                  onClick={() => removeLine(li.clientId)}
                                  className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-red-600 transition-colors hover:bg-red-50 hover:border-red-300 hover:text-red-700 disabled:opacity-50"
                                  disabled={saving}
                                  title="Remove"
                                  aria-label="Remove line item"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                          {showDetails && (
                            <div className={`flex gap-2 pb-3 border-b border-gray-100 ${!li.included ? 'bg-gray-100/80 opacity-70' : ''}`}>
                              <div className="w-10 shrink-0" />
                              <div className="flex-1 min-w-0 text-xs text-gray-500 -mt-1">
                                {li.source === 'booking' ? 'From booking' : 'Custom'}
                                {li.professionalName ? ` • Professional: ${li.professionalName}` : ''}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="text-xs text-gray-500 mt-3">
                    Tip: prices are editable for the invoice and may differ from booking estimates.
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <StickyNote className="w-5 h-5 text-gray-600" />
                    Notes
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Customer note (shows on invoice)</label>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        disabled={saving}
                      />
                      {!onlinePaymentsEnabled && (
                        <div
                          role="alert"
                          className="mt-3 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3.5 text-amber-900"
                        >
                          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                          </div>
                          <div className="min-w-0 text-xs leading-relaxed text-amber-900">
                            <p className="text-sm font-semibold text-amber-950">Online payments are not enabled</p>
                            <p className="mt-1">
                              Stripe has not been set up for this business. AACore Booking can send this invoice, but the
                              customer will not be able to pay online through the platform.
                            </p>
                            <p className="mt-2">
                              Consider adding payment options in the customer note above before sending the invoice.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Internal note (staff only)</label>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        rows={3}
                        value={internalNotes}
                        onChange={(e) => setInternalNotes(e.target.value)}
                        disabled={saving}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-gray-600" />
                    Totals
                  </h2>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">Subtotal</div>
                      <div className="font-semibold text-gray-900">${formatMoney(subTotal)}</div>
                    </div>

                    <div className="grid grid-cols-2 items-center gap-3">
                      <div className="text-sm text-gray-600">Discount</div>
                      <div className="relative">
                        <span className="absolute left-3 top-2.5 text-gray-500">$</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="0.00"
                          className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right"
                          value={centsDigitsToMoneyString(discount)}
                          onChange={(e) => setDiscount(e.target.value.replace(/\D/g, ''))}
                          disabled={saving}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 items-center gap-3">
                      <div className="text-sm text-gray-600">Tax (%)</div>
                      <div className="relative">
                        <span className="absolute right-3 top-2.5 text-gray-500">%</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="0.00"
                          className="w-full pr-7 pl-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right"
                          value={percentDigitsToPercentString(taxPercent)}
                          onChange={(e) => setTaxPercent(e.target.value.replace(/\D/g, ''))}
                          disabled={saving}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 items-center gap-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="include-tip-create"
                          checked={includeTip}
                          onChange={(e) => setIncludeTip(e.target.checked)}
                          disabled={saving}
                          className="h-4 w-4 accent-[#0B4778]"
                        />
                        <label htmlFor="include-tip-create" className="text-sm text-gray-600">Tip</label>
                      </div>
                      <div className="relative">
                        <span className="absolute left-3 top-2.5 text-gray-500">$</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="0.00"
                          className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right disabled:bg-gray-100"
                          value={centsDigitsToMoneyString(tip)}
                          onChange={(e) => setTip(e.target.value.replace(/\D/g, ''))}
                          disabled={saving || !includeTip}
                        />
                      </div>
                    </div>

                    <div className="h-px bg-gray-200 my-2"></div>

                    <div className="flex items-center justify-between">
                      <div className="text-base font-semibold text-gray-900">Total</div>
                      <div className="text-2xl font-semibold text-gray-900">${formatMoney(total)}</div>
                    </div>

                    {appointment?.receivedDepositAmount != null && Number(appointment.receivedDepositAmount) > 0 && (
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">Deposit</div>
                        <div className="font-semibold text-gray-900">-${formatMoney(Number(appointment.receivedDepositAmount))}</div>
                      </div>
                    )}

                    <div className="rounded-lg px-4 py-3 mt-2 bg-[#0B4778]/10 border border-[#0B4778]/20">
                      <div className="flex items-center justify-between">
                        <div className="text-base font-semibold text-[#0B4778]">Invoice Due</div>
                        <div className="text-xl font-bold text-[#0B4778]">
                          ${formatMoney(Math.max(0, total - (appointment?.receivedDepositAmount != null && Number(appointment.receivedDepositAmount) > 0 ? Number(appointment.receivedDepositAmount) : 0)))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                  {collectionMethod === 'send_invoice' ? (
                    <button
                      type="button"
                      onClick={() => handleSave('SENT')}
                      className="aacore-btn2-primary inline-flex items-center justify-center gap-2 w-full disabled:opacity-50"
                      disabled={!canSendInvoice || saving}
                      title={
                        saving
                          ? 'Sending…'
                          : !customerEmail.trim()
                            ? 'Customer email is required to send'
                            : !canSendInvoice
                              ? 'Invoice must have at least one line item and required fields'
                              : 'Send invoice by email'
                      }
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                      ) : (
                        <Send className="w-4 h-4 shrink-0" />
                      )}
                      Send Invoice
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleSave()}
                      className="aacore-btn2-primary inline-flex items-center justify-center gap-2 w-full disabled:opacity-50"
                      disabled={!canSave || saving}
                      title={saving ? 'Saving…' : !canSave ? 'Include at least one line item and fill in required fields' : 'Continue to payment to charge the customer'}
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                      ) : (
                        <CreditCard className="w-4 h-4 shrink-0" />
                      )}
                      Continue to Payment
                    </button>
                  )}
                  {!canSave && collectionMethod === 'charge_now' && (
                    <div className="text-xs text-gray-500 mt-2">
                      Include at least one line item and fill in required fields to continue.
                    </div>
                  )}
                  {!canSendInvoice && collectionMethod === 'send_invoice' && (
                    <div className="text-xs text-gray-500 mt-2">
                      Include at least one line item, customer email, and required fields to send the invoice.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {openItemDropdownClientId &&
        itemDropdownAnchorRect &&
        (() => {
          const li = lineItems.find((l) => l.clientId === openItemDropdownClientId);
          if (!li) return null;
          const q = (li.description || '').trim().toLowerCase();
          const filtered = q
            ? businessServices.filter((s) => s.name.toLowerCase().includes(q))
            : businessServices;
          return createPortal(
            <ul
              data-item-dropdown-list
              role="listbox"
              className="fixed z-[9999] max-h-56 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg py-1"
              style={{
                top: itemDropdownAnchorRect.top + itemDropdownAnchorRect.height + 4,
                left: itemDropdownAnchorRect.left,
                width: Math.max(itemDropdownAnchorRect.width, 200),
              }}
            >
              {filtered.map((s) => (
                <li
                  key={s.id}
                  role="option"
                  className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-gray-900"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    updateLine(li.clientId, {
                      description: s.name,
                      unitPriceCents: s.price != null ? moneyToCentsDigits(s.price) : li.unitPriceCents,
                    });
                    setOpenItemDropdownClientId(null);
                  }}
                >
                  <span className="font-medium">{s.name}</span>
                  {s.price != null && (
                    <span className="ml-2 text-gray-500">${Number(s.price).toFixed(2)}</span>
                  )}
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="px-3 py-2 text-gray-500 text-sm">No matching services</li>
              )}
            </ul>,
            document.body
          );
        })()}
    </div>
  );
};

export default CreateInvoicePage;

